import dayjs from "dayjs";
import { SPACE_HOME_CACHE } from "./home/Config";
import type {
  GetHomeDataResult,
  SpaceHomeCacheScope,
  SpaceHomeCacheSnapshot,
} from "./SpaceType";

const DT = "YYYY-MM-DD HH:mm:ss";

function cacheStorageKey(scope: SpaceHomeCacheScope): string {
  return `${SPACE_HOME_CACHE.keyPrefix}.v${SPACE_HOME_CACHE.version}:${scope.customerKey}:${scope.spaceId}`;
}

export function buildSpaceHomeCacheScope(
  spaceId: string,
  customerId?: string | null
): SpaceHomeCacheScope {
  const cid = customerId?.trim();
  return {
    customerKey: cid || "guest",
    spaceId: spaceId.trim(),
  };
}

export function loadSpaceHomeCache(
  scope: SpaceHomeCacheScope
): SpaceHomeCacheSnapshot | null {
  if (typeof localStorage === "undefined") {
    return null;
  }
  const raw = localStorage.getItem(cacheStorageKey(scope));
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as SpaceHomeCacheSnapshot;
    if (parsed.version !== SPACE_HOME_CACHE.version) {
      localStorage.removeItem(cacheStorageKey(scope));
      return null;
    }
    if (parsed.customerKey !== scope.customerKey || parsed.spaceId !== scope.spaceId) {
      return null;
    }
    if (!parsed.homeData || typeof parsed.homeData !== "object") {
      return null;
    }
    return parsed;
  } catch {
    localStorage.removeItem(cacheStorageKey(scope));
    return null;
  }
}

export function saveSpaceHomeCache(
  scope: SpaceHomeCacheScope,
  homeData: GetHomeDataResult
): void {
  if (typeof localStorage === "undefined") {
    return;
  }
  const snapshot: SpaceHomeCacheSnapshot = {
    version: SPACE_HOME_CACHE.version,
    customerKey: scope.customerKey,
    spaceId: scope.spaceId,
    homeData,
    updatedAt: dayjs().format(DT),
  };
  try {
    localStorage.setItem(cacheStorageKey(scope), JSON.stringify(snapshot));
  } catch {
    // ignore quota errors
  }
}

export function clearSpaceHomeCache(scope: SpaceHomeCacheScope): void {
  if (typeof localStorage === "undefined") {
    return;
  }
  localStorage.removeItem(cacheStorageKey(scope));
}
